import { UiKit, Button } from '@datavant/uikit';
import { Field, FieldInputProps, Form, Formik, FormikProps } from 'formik';
import { tRPCProxyClient } from '../../../utils/trpc';
import * as Yup from 'yup';
import React from 'react';

const PROHIBITED_PUBLIC_DOMAINS = [
    'icloud.com',
    'me.com',
    'mac.com',
    'protonmail.com',
    'zoho.com',
    'gmx.com',
    'mail.com',
    'yandex.com',
    'tutanota.com',
    'fastmail.com',
    'hushmail.com',
    'rackspace.com',
    'mail.ru',
    'comcast.net',
    'verizon.net',
    'att.net',
    'cox.net',
    'spectrum.net',
];

export const isProhibitedEmailDomain = (email: string): boolean => {
    const domain = email.split('@')?.[1]?.toLowerCase();
    if (!domain) {
        return false;
    }
    return PROHIBITED_PUBLIC_DOMAINS.some(publicDomain => publicDomain === domain);
};

type FormProps = {
    email: string;
};

const FormSchema = Yup.object().shape({
    email: Yup.string()
        .email('Please enter a valid email.')
        .required('Email is required')
        .test(
            'isNotProhibitedDomain',
            'This email uses a prohibited domain. Please reach out to customer service via help options below.',
            (value, _context) => !isProhibitedEmailDomain(value) && !value.includes('+')
        ),
});

type EmailDiscoveryProps = {
    /** Perform a side effect after email discovery result */
    onEmailDiscovered?: ({ doesUserExist, email }: { doesUserExist: boolean; email: string }) => void;
};

/** Discovers if an email is associated with an idsb-portal user */
export const EmailDiscovery = ({ onEmailDiscovered }: EmailDiscoveryProps) => {
    return (
        <Formik<FormProps>
            initialValues={{ email: '' }}
            onSubmit={async ({ email }, actions) => {
                const { doesUserExist } = await tRPCProxyClient.tenant.doesUserExist.query({ email });
                actions.setSubmitting(false);
                onEmailDiscovered?.({ doesUserExist, email });
            }}
            validationSchema={FormSchema}
        >
            {({ isSubmitting, isValid, values: { email } }) => (
                <Form>
                    <UiKit.Chakra.VStack gap={8} width={400} px={42}>
                        <UiKit.Chakra.Text fontSize={16} fontWeight={500} lineHeight={'24px'}>
                            Log in or Sign Up New Account
                        </UiKit.Chakra.Text>
                        <Field name={'email'}>
                            {({
                                field,
                                form,
                            }: {
                                field: FieldInputProps<FormProps['email']>;
                                form: FormikProps<FormProps>;
                            }) => (
                                <UiKit.Chakra.FormControl
                                    isRequired
                                    isInvalid={!!form.errors.email && !!form.touched.email}
                                >
                                    <UiKit.Chakra.FormLabel style={{ color: '#4f4e4c', fontWeight: 500 }}>
                                        Work Email
                                    </UiKit.Chakra.FormLabel>
                                    <UiKit.Chakra.Text fontSize={12} pb={5} mt={-2} color={'#4f4E4c'}>
                                        Enter your organization work email to establish login credentials and
                                        verify your identity.
                                    </UiKit.Chakra.Text>
                                    <UiKit.Chakra.Input
                                        {...field}
                                        type="email"
                                        fontSize={14}
                                    ></UiKit.Chakra.Input>
                                    <UiKit.Chakra.FormErrorMessage>
                                        {form.errors.email}
                                    </UiKit.Chakra.FormErrorMessage>
                                </UiKit.Chakra.FormControl>
                            )}
                        </Field>
                        <Button
                            type="submit"
                            isLoading={isSubmitting}
                            backgroundColor={UiKit.consistency.brandColors.primaryButton.background}
                            color={'white'}
                            isDisabled={!isValid || !email}
                            borderRadius={24}
                            height={50}
                            fontFamily={'Inter, montserrat-okta, Arial, Helvetica, sans-serif'}
                        >
                            Continue
                        </Button>
                    </UiKit.Chakra.VStack>
                </Form>
            )}
        </Formik>
    );
};
